import React, { useMemo, useState, Children } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProductCard } from '../components/product/ProductCard';
import { FilterPanel } from '../components/filters/FilterPanel';
import { FilterGroup, CheckboxFilter, PriceRangeFilter } from '../components/filters/FilterGroup';
import { Select } from '../components/ui/Select';
import { getCategoryBySlug } from '../data/categories';
import { getProductsByCategory, products } from '../data/products';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
type SortOption = 'popular' | 'new' | 'price-asc' | 'price-desc';
export function CategoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const category = getCategoryBySlug(slug || '');
  const categoryProducts = slug ? getProductsByCategory(slug) : [];
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100000]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  // Get all products for this category (or all products if no category)
  const allProducts = category ? categoryProducts : products;
  // Calculate price range
  const minPrice = Math.min(...allProducts.map(p => p.price));
  const maxPrice = Math.max(...allProducts.map(p => p.price));
  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];
    // Filter by price
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    // Filter by stock
    if (inStockOnly) {
      result = result.filter(p => p.inStock);
    }
    // Sort
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'new':
        result.sort((a, b) => a.badge === 'Новинка' ? -1 : 1);
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [allProducts, sortBy, priceRange, inStockOnly]);
  const handleResetFilters = () => {
    setPriceRange([minPrice, maxPrice]);
    setInStockOnly(false);
    setSelectedBrands([]);
  };
  const sortOptions = [{
    value: 'popular',
    label: 'Популярні'
  }, {
    value: 'new',
    label: 'Нові'
  }, {
    value: 'price-asc',
    label: 'Дешевші'
  }, {
    value: 'price-desc',
    label: 'Дорожчі'
  }];
  if (!category && slug) {
    return <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-id="element-517">
        <div className="text-center px-4" data-id="element-518">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-519">
            Категорію не знайдено
          </h1>
          <Link to="/catalog" className="text-blue-500 hover:text-blue-400" data-id="element-520">
            Повернутися до каталогу
          </Link>
        </div>
      </main>;
  }
  return <main className="min-h-screen bg-[#0a0a0a]" data-id="element-521">
      {/* Breadcrumbs */}
      <div className="bg-[#0a0a0a] border-b border-[#1a1a1a]" data-id="element-522">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-523">
          <nav className="flex items-center gap-2 text-sm overflow-x-auto" data-id="element-524">
            <Link to="/" className="text-gray-500 hover:text-gray-300 whitespace-nowrap" data-id="element-525">
              Головна
            </Link>
            <span className="text-gray-700" data-id="element-526">/</span>
            <Link to="/catalog" className="text-gray-500 hover:text-gray-300 whitespace-nowrap" data-id="element-527">
              Каталог
            </Link>
            {category && <>
                <span className="text-gray-700" data-id="element-528">/</span>
                <span className="text-white font-medium whitespace-nowrap" data-id="element-529">
                  {category.name}
                </span>
              </>}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-530">
        {/* Header */}
        <div className="mb-6 lg:mb-8" data-id="element-531">
          <h1 className="text-2xl lg:text-4xl font-bold text-white mb-2" data-id="element-532">
            {category?.name || 'Всі товари'}
          </h1>
          {category && <p className="text-gray-400 text-sm lg:text-base" data-id="element-533">
              {category.description}
            </p>}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-534">
          {/* Filters Sidebar - Hidden on mobile */}
          <div className="hidden lg:block" data-id="element-535">
            <FilterPanel onReset={handleResetFilters} className="w-64 flex-shrink-0" data-id="element-536">
              <FilterGroup title="Ціна" data-id="element-537">
                <PriceRangeFilter min={minPrice} max={maxPrice} value={priceRange} onChange={setPriceRange} data-id="element-538" />
              </FilterGroup>

              <FilterGroup title="Наявність" data-id="element-539">
                <CheckboxFilter label="Тільки в наявності" checked={inStockOnly} onChange={setInStockOnly} data-id="element-540" />
              </FilterGroup>

              <FilterGroup title="Бренд" data-id="element-541">
                <CheckboxFilter label="Intex" checked={false} onChange={() => {}} count={12} data-id="element-542" />
                <CheckboxFilter label="Bestway" checked={false} onChange={() => {}} count={8} data-id="element-543" />
                <CheckboxFilter label="Hayward" checked={false} onChange={() => {}} count={5} data-id="element-544" />
                <CheckboxFilter label="Emaux" checked={false} onChange={() => {}} count={7} data-id="element-545" />
              </FilterGroup>

              <FilterGroup title="Рейтинг" data-id="element-546">
                <CheckboxFilter label="4+ зірки" checked={false} onChange={() => {}} data-id="element-547" />
                <CheckboxFilter label="3+ зірки" checked={false} onChange={() => {}} data-id="element-548" />
              </FilterGroup>
            </FilterPanel>
          </div>

          {/* Products Grid */}
          <div className="flex-1 min-w-0" data-id="element-549">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4" data-id="element-550">
              <FilterPanel onReset={handleResetFilters} data-id="element-551">
                <FilterGroup title="Ціна" data-id="element-552">
                  <PriceRangeFilter min={minPrice} max={maxPrice} value={priceRange} onChange={setPriceRange} data-id="element-553" />
                </FilterGroup>

                <FilterGroup title="Наявність" data-id="element-554">
                  <CheckboxFilter label="Тільки в наявності" checked={inStockOnly} onChange={setInStockOnly} data-id="element-555" />
                </FilterGroup>

                <FilterGroup title="Бренд" data-id="element-556">
                  <CheckboxFilter label="Intex" checked={false} onChange={() => {}} count={12} data-id="element-557" />
                  <CheckboxFilter label="Bestway" checked={false} onChange={() => {}} count={8} data-id="element-558" />
                  <CheckboxFilter label="Hayward" checked={false} onChange={() => {}} count={5} data-id="element-559" />
                  <CheckboxFilter label="Emaux" checked={false} onChange={() => {}} count={7} data-id="element-560" />
                </FilterGroup>

                <FilterGroup title="Рейтинг" data-id="element-561">
                  <CheckboxFilter label="4+ зірки" checked={false} onChange={() => {}} data-id="element-562" />
                  <CheckboxFilter label="3+ зірки" checked={false} onChange={() => {}} data-id="element-563" />
                </FilterGroup>
              </FilterPanel>
            </div>

            {/* Toolbar - Hidden on mobile */}
            <div className="hidden lg:flex items-center justify-between gap-4 mb-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4" data-id="element-564">
              <p className="text-sm text-gray-400" data-id="element-565">
                Знайдено{' '}
                <span className="font-semibold text-white" data-id="element-566">
                  {filteredProducts.length}
                </span>{' '}
                товарів
              </p>
              <div className="flex items-center gap-4" data-id="element-567">
                <Select options={sortOptions} value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="w-40" data-id="element-568" />
              </div>
            </div>

            {/* Products */}
            {filteredProducts.length > 0 ? <motion.div initial="hidden" animate="visible" variants={containerVariants} className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-569">
                {filteredProducts.map(product => <motion.div key={product.id} variants={itemVariants} data-id="element-570">
                    <ProductCard product={product} data-id="element-571" />
                  </motion.div>)}
              </motion.div> : <div className="text-center py-16 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl" data-id="element-572">
                <p className="text-gray-400 mb-4" data-id="element-573">
                  Товарів за вашим запитом не знайдено
                </p>
                <button onClick={handleResetFilters} className="text-blue-500 hover:text-blue-400 font-medium" data-id="element-574">
                  Скинути фільтри
                </button>
              </div>}
          </div>
        </div>
      </div>
    </main>;
}